import API_BASE from '../../utils/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    eventDate: '',
    eventType: 'other',
    isSchoolWide: false
  });

  const eventTypes = [
    { value: 'exam', label: 'Exam' },
    { value: 'holiday', label: 'Holiday' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'sports', label: 'Sports' },
    { value: 'cultural', label: 'Cultural' },
    { value: 'field_trip', label: 'Field Trip' },
    { value: 'other', label: 'Other' }
  ];

  const eventTypeColors = {
    exam: 'bg-red-100 text-red-800',
    holiday: 'bg-green-100 text-green-800',
    meeting: 'bg-yellow-100 text-yellow-800',
    sports: 'bg-blue-100 text-blue-800',
    cultural: 'bg-purple-100 text-purple-800',
    field_trip: 'bg-orange-100 text-orange-800',
    other: 'bg-gray-100 text-gray-800'
  };

  useEffect(() => {
    fetchClassInfo();
    fetchEvents();
  }, []);

  const fetchClassInfo = async () => {
    try {
      const res = await axios.get(`${API_BASE}/classes/my-class`);
      setClassInfo(res.data);
    } catch (err) {
      console.error('Error fetching class:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_BASE}/events`);
      setEvents(res.data);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        classId: form.isSchoolWide ? undefined : classInfo?._id
      };

      if (editId) {
        await axios.put(`${API_BASE}/events/${editId}`, payload);
      } else {
        await axios.post(`${API_BASE}/events`, payload);
      }

      resetForm();
      fetchEvents();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving event');
    }
  };

  const resetForm = () => {
    setForm({ title: '', description: '', eventDate: '', eventType: 'other', isSchoolWide: false });
    setEditId(null);
    setShowForm(false);
  };

  const handleEdit = (event) => {
    setForm({
      title: event.title,
      description: event.description,
      eventDate: new Date(event.eventDate).toISOString().split('T')[0],
      eventType: event.eventType,
      isSchoolWide: event.isSchoolWide
    });
    setEditId(event._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API_BASE}/events/${id}`);
      fetchEvents();
    } catch (err) {
      alert('Error deleting event');
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const isUpcoming = (d) => new Date(d) >= new Date(new Date().setHours(0, 0, 0, 0));

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="flex justify-between items-center px-6 py-3">
            <h1 className="text-xl font-semibold text-gray-800">Events</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Upcoming Events</h2>
              {classInfo && (
                <p className="text-gray-600">Class: {classInfo.className} - {classInfo.section}</p>
              )}
            </div>
            <button
              onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
            >
              {showForm ? 'Cancel' : '+ Create Event'}
            </button>
          </div>

          {showForm && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">{editId ? 'Edit Event' : 'Create New Event'}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Event Title</label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="e.g. Annual Sports Day"
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Event Date</label>
                    <input
                      type="date"
                      value={form.eventDate}
                      onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Event details..."
                    className="w-full border rounded-lg px-3 py-2"
                    rows="3"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
                    <select
                      value={form.eventType}
                      onChange={(e) => setForm({ ...form, eventType: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      {eventTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.isSchoolWide}
                        onChange={(e) => setForm({ ...form, isSchoolWide: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">School-wide event (visible to all classes)</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button type="submit" className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700">
                    {editId ? 'Update Event' : 'Create Event'}
                  </button>
                  <button type="button" onClick={resetForm} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              No events created yet.
            </div>
          ) : (
            <div className="space-y-4">
              {events.map((event) => (
                <div key={event._id} className={`bg-white rounded-lg shadow p-5 border-l-4 ${isUpcoming(event.eventDate) ? 'border-indigo-500' : 'border-gray-300'}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900 text-lg">{event.title}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${eventTypeColors[event.eventType] || eventTypeColors.other}`}>
                          {event.eventType.replace('_', ' ')}
                        </span>
                        {event.isSchoolWide && (
                          <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full text-xs">School-wide</span>
                        )}
                        {!isUpcoming(event.eventDate) && (
                          <span className="bg-gray-100 text-gray-500 px-2 py-1 rounded-full text-xs">Past</span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-2">{event.description}</p>
                      <p className="text-sm text-gray-500">
                        📅 {formatDate(event.eventDate)}
                        {event.classId && ` | 🏫 ${event.classId.className} - ${event.classId.section}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleEdit(event)}
                        className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded text-sm hover:bg-yellow-200"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(event._id)}
                        className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Events;
